import React from "react";
import { Link } from "react-router-dom";
import "@/pages/public/approved.css";
import "@/pages/public/carousel.css";
import "@/pages/public/polish.css";
import "@/pages/public/drop-builder.css";
import "@/pages/public/reach.css";
import "@/pages/public/inquire.css";
import CosmosCanvas from "@/pages/public/CosmosCanvas";
import RewardWheel from "@/pages/public/RewardWheel";
import CinematicLayer from "@/pages/public/CinematicLayer";
import RocketIntro from "@/pages/public/RocketIntro";
import { DropProvider } from "@/pages/public/DropBuilderCtx";
import { DropPill, DropNavBadge, DropDrawer } from "@/pages/public/DropBuilder";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ASSET = "/2high-assets";

export const PublicCtx = React.createContext({
  content: {}, products: [], testimonials: [], landed: [], categories: [], reload: () => {}
});

export function usePublicData() {
  return React.useContext(PublicCtx);
}

export function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/")) return BACKEND + path;
  // Fallback: legacy placeholder paths like "../img/xyz.webp" → serve from /2high-assets/img/
  if (path.startsWith("../img/")) return ASSET + "/img/" + path.split("/").pop();
  if (path.startsWith("../assets/img/")) return ASSET + "/assets/img/" + path.split("/").pop();
  return BACKEND + "/" + path;
}

export function isVideoUrl(url) {
  if (!url) return false;
  return (
    /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|$)/i.test(url) ||
    /\/api\/media\/original\//.test(url) ||
    /\/video\/upload\//.test(url)            // Cloudinary video URLs
  );
}

export function availClass(av) {
  const s = (av || "").toLowerCase();
  if (s.includes("sold")) return "sold";
  if (s.includes("limit")) return "low";
  if (s.includes("coming")) return "low";
  return "";
}

export function PublicLayout({ children }) {
  const [data, setData] = React.useState({
    content: {}, products: [], testimonials: [], landed: [], categories: []
  });
  const [ageOk, setAgeOk] = React.useState(() => {
    // Both session AND localStorage must be set so the user gets the full
    // rocket→age-gate ceremony on every fresh tab/session, but in-tab
    // refreshes don't re-prompt them.
    try {
      const sess = sessionStorage.getItem("2h-age-ok-session") === "1";
      return sess && localStorage.getItem("2h-age-ok") === "1";
    } catch (_) { return false; }
  });
  const [rocketDone, setRocketDone] = React.useState(() => {
    try { return !!sessionStorage.getItem("2h_rocket_seen_v1"); } catch (_) { return false; }
  });

  const reload = React.useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/public/content`);
      const d = await r.json();
      setData({
        content: d.content || {},
        products: d.products || [],
        testimonials: d.testimonials || [],
        landed: d.landed || [],
        categories: d.categories || [],
        wheel_prizes: d.wheel_prizes || [],
      });
    } catch (e) { /* ignore */ }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Lock body scroll while age gate is visible
  React.useEffect(() => {
    document.body.style.overflow = ageOk ? "" : "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [ageOk]);

  const acceptAge = () => {
    try {
      localStorage.setItem("2h-age-ok", "1");
      sessionStorage.setItem("2h-age-ok-session", "1");
    } catch (_) {}
    setAgeOk(true);
  };

  return (
    <PublicCtx.Provider value={{ ...data, reload }}>
      <DropProvider>
      <div className="th-public-root">
        <RocketIntro onDone={() => setRocketDone(true)} />
        {rocketDone && !ageOk && <AgeGate content={data.content} onAccept={acceptAge} />}

        {/* Cinematic intro mask + scroll reveals + magnetic buttons — fires after age accept */}
        <CinematicLayer active={ageOk} />

        {/* Cosmic atmosphere — drifting starfield + soft glow */}
        <CosmosCanvas />
        <div className="bg-smoke" aria-hidden="true" />

        {/* Top nav */}
        <TopNav content={data.content} />

        <div className="layer">
          {children}
          <Footer content={data.content} />
        </div>

        {/* Sticky bottom bar with animated pill */}
        <StickyBar />

        {/* Reward wheel — once per member per campaign, admin-controllable */}
        <RewardWheel ageOk={ageOk} enabled content={data.content} prizes={data.wheel_prizes} />

        {/* Inquiry FAB + panel live globally */}
        <InquireFabButton />

        {/* Drop Builder · floating pill + slide-out drawer */}
        <DropPill />
        <DropDrawer />
      </div>
      </DropProvider>
    </PublicCtx.Provider>
  );
}

function AgeGate({ content, onAccept }) {
  const c = content || {};
  return (
    <div className="age-gate" id="ageGate" data-testid="age-gate" style={{ display: "flex" }}>
      <div className="age-card">
        <img src={`${ASSET}/assets/img/logo-md.webp`} alt="2High" loading="eager" />
        <div className="ml">{c.age_eye || "— 2HIGH · MEMBERS ACCESS —"}</div>
        <h2>
          <span>{c.age_h || "Welcome to"}</span> <em>{c.age_h_em || "2High"}</em>
        </h2>
        <p>{c.age_p || "You must be 21 or older to enter. By continuing, you confirm you're of legal age and acknowledge our private members policy."}</p>
        <div className="age-row">
          <button className="btn solid" onClick={onAccept} data-testid="age-yes-btn">{c.age_yes || "I'm 21+ · Enter"}</button>
          <button
            className="btn"
            onClick={() => { window.location.href = "https://www.google.com"; }}
            data-testid="age-no-btn"
          >
            {c.age_no || "Exit"}
          </button>
        </div>
        <div className="legal">{c.age_legal || "Private collection · 21+ only · Not for resale"}</div>
      </div>
    </div>
  );
}

function TopNav({ content }) {
  const c = content || {};
  return (
    <nav className="nav" data-testid="top-nav">
      <Link to="/" className="brand" data-testid="brand-home">
        <img src={`${ASSET}/assets/img/logo-md.webp`} alt="2High" />
        <b>2<i>High</i></b>
      </Link>
      <div className="nav-links">
        <Link to="/menu" data-testid="nav-menu">Menu</Link>
        <Link to="/menu#flower" data-testid="nav-breakdown">Build Drop</Link>
        <Link to="/menu#wholesale" data-testid="nav-wholesale">Wholesale</Link>
        <Link to="/#owner" data-testid="nav-story">Story</Link>
      </div>
      <div className="nav-right">
        <DropNavBadge />
        <span className="nav-pill" data-testid="members-pill">Members Open</span>
      </div>
    </nav>
  );
}

function StickyBar() {
  const ref = React.useRef(null);
  const pillRef = React.useRef(null);

  React.useEffect(() => {
    const bar = ref.current;
    const pill = pillRef.current;
    if (!bar || !pill) return;
    // Force display:flex !important — beats approved.css late rule that hides .sbar
    bar.style.setProperty("display", "flex", "important");

    const links = Array.from(bar.querySelectorAll("a, button"));
    if (!links.length) return;

    function movePillTo(el) {
      if (!el) return;
      const br = bar.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      pill.style.width = er.width + "px";
      pill.style.transform = `translateX(${er.left - br.left - 6}px)`;
    }

    // Init: highlight whichever section is in view
    const sections = links
      .map((a) => {
        const href = a.getAttribute("href") || "";
        const id = href.includes("#") ? href.split("#").pop() : "";
        return id ? document.getElementById(id) : null;
      })
      .map((el, i) => ({ el, link: links[i] }))
      .filter((x) => x.el);

    function update() {
      const y = window.scrollY + window.innerHeight * 0.4;
      let active = links[0];
      sections.forEach((s) => { if (s.el.offsetTop <= y) active = s.link; });
      links.forEach((a) => a.classList.toggle("act", a === active));
      movePillTo(active);
    }

    requestAnimationFrame(update);
    const onScroll = () => update();
    const onResize = () => update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    const enterHandlers = links.map((a) => {
      const h = () => movePillTo(a);
      a.addEventListener("pointerenter", h);
      return [a, h];
    });
    const leaveHandlers = links.map((a) => {
      const h = () => {
        const cur = bar.querySelector("a.act, button.act") || links[0];
        movePillTo(cur);
      };
      a.addEventListener("pointerleave", h);
      return [a, h];
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      enterHandlers.forEach(([a, h]) => a.removeEventListener("pointerenter", h));
      leaveHandlers.forEach(([a, h]) => a.removeEventListener("pointerleave", h));
    };
  }, []);

  const fireInquire = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("2high:inquire", { detail: {} }));
  };

  return (
    <div className="sbar" ref={ref} data-testid="sticky-bar" style={{ display: "flex" }}>
      <span className="sbar-pill" ref={pillRef} style={{ width: 0 }} />
      <a href="/#hero" className="act" data-testid="sbar-home">
        <svg viewBox="0 0 24 24"><path d="M3 11l9-8 9 8v10a2 2 0 0 1-2 2h-4v-7H10v7H6a2 2 0 0 1-2-2z"/></svg>
        <span>Home</span>
      </a>
      <a href="/menu#flower" data-testid="sbar-shelf">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/></svg>
        <span>Shelf</span>
      </a>
      <a href="/menu#flower" data-testid="sbar-flight">
        <svg viewBox="0 0 24 24"><path d="M12 2l8 18-8-4-8 4z"/></svg>
        <span>Flight</span>
      </a>
      <a href="/menu#wholesale" data-testid="sbar-wholesale">
        <svg viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
        <span>Bulk</span>
      </a>
      <button type="button" onClick={fireInquire} data-testid="sbar-inquire" className="sbar-inq">
        <svg viewBox="0 0 24 24"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
        <span>Inquire</span>
      </button>
    </div>
  );
}

function Footer({ content }) {
  const c = content || {};

  const socials = [
    { id: "telegram",  label: "Telegram",  handle: c.contact_telegram_handle,  url: c.contact_telegram_url,
      icon: "M21.5 4.5l-19 7 6 2 2.5 7 3.5-4 4.5 4 2.5-16z" },
    { id: "whatsapp",  label: "WhatsApp",  handle: c.contact_whatsapp ? `+${c.contact_whatsapp}` : "",
      url: c.contact_whatsapp_url,
      icon: "M20.5 3.5A11 11 0 0 0 4.6 18l-1.6 4.5 4.7-1.5A11 11 0 1 0 20.5 3.5zM12 20a8 8 0 0 1-4.2-1.2l-.3-.2-2.8.9.9-2.7-.2-.3A8 8 0 1 1 12 20zm4.5-5.5c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.7.9-.3.2-.5.1a6.5 6.5 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.4.1-.2 0-.3 0-.5l-.8-1.9c-.2-.4-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2c0 1.3.9 2.6 1 2.8s1.8 2.7 4.4 3.8c1.6.7 2.2.7 3 .6.5-.1 1.4-.6 1.7-1.2.2-.6.2-1.1.2-1.2s-.2-.2-.4-.3z" },
    { id: "sms",       label: "Text",       handle: c.contact_phone_handle || c.contact_phone, url: c.contact_sms_url,
      icon: "M3 5h18v12H7l-4 4V5z" },
    { id: "instagram", label: "Instagram", handle: c.contact_instagram_handle, url: c.contact_instagram_url,
      icon: "M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm5 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm5.5-.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" },
    { id: "tiktok",    label: "TikTok",    handle: c.contact_tiktok_handle,    url: c.contact_tiktok_url,
      icon: "M16 3v3a5 5 0 0 0 5 5v3a8 8 0 0 1-5-1.7V16a6 6 0 1 1-6-6h.5v3.2H10a3 3 0 1 0 3 3V3h3z" },
  ].filter((s) => s.url);

  const tg = c.contact_telegram_url;
  const sms = c.contact_sms_url;

  return (
    <footer className="foot foot-rich" data-testid="public-footer">
      {/* ---------- Reach Us · contact block ---------- */}
      <section className="reach" data-testid="reach-block">
        <div className="reach__head">
          <div className="reach__eye">— REACH US · 21+ · MEMBERS ONLY</div>
          <h2 className="reach__h">
            Drop us <em>a message.</em>
          </h2>
          <p className="reach__sub">
            Every drop is confirmed by hand. Text us on Telegram or shoot a quick
            SMS and we'll lock in your rotation, share the live menu, and walk you
            through the next ship. No bots, no checkouts — just real ops.
          </p>
        </div>

        <div className="reach__how">
          <div className="reach-step">
            <span className="reach-step__n">01</span>
            <h4>Open the chat</h4>
            <p>Telegram is fastest — DMs open 24/7. SMS works too.</p>
          </div>
          <div className="reach-step">
            <span className="reach-step__n">02</span>
            <h4>Build your drop</h4>
            <p>Pick anything from the menu or just say what you're after. We'll quote, confirm, and lock the batch.</p>
          </div>
          <div className="reach-step">
            <span className="reach-step__n">03</span>
            <h4>Get it discreet</h4>
            <p>Private ship · vacuum-sealed · tracked from door to door. We confirm landing.</p>
          </div>
        </div>

        {/* Primary CTAs */}
        <div className="reach__ctas">
          {tg && (
            <a href={tg} target="_blank" rel="noreferrer" className="reach__cta reach__cta--gold" data-testid="reach-cta-telegram">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M21.5 4.5l-19 7 6 2 2.5 7 3.5-4 4.5 4 2.5-16z" />
              </svg>
              <span>
                <small>Message on Telegram</small>
                <strong>{c.contact_telegram_handle || "@2high"}</strong>
              </span>
            </a>
          )}
          {sms && (
            <a href={sms} className="reach__cta reach__cta--ghost" data-testid="reach-cta-sms">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 5h18v12H7l-4 4V5z" />
              </svg>
              <span>
                <small>Text us</small>
                <strong>{c.contact_phone_handle || c.contact_phone}</strong>
              </span>
            </a>
          )}
        </div>

        {/* Socials row */}
        {socials.length > 0 && (
          <div className="reach__socials" data-testid="reach-socials">
            <span className="reach__socials-label">Follow the drops</span>
            <div className="reach__socials-row">
              {socials.map((s) => (
                <a key={s.id} href={s.url} target="_blank" rel="noreferrer"
                  className="reach-social" data-testid={`reach-social-${s.id}`}
                  aria-label={`${s.label} ${s.handle || ""}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={s.icon} />
                  </svg>
                  <span className="reach-social__label">{s.label}</span>
                  {s.handle && <span className="reach-social__handle">{s.handle}</span>}
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ---------- Mini nav ---------- */}
      <div className="links">
        <Link to="/menu" data-testid="foot-menu">Menu</Link>
        <Link to="/menu#flower" data-testid="foot-build">Build Drop</Link>
        <Link to="/menu#wholesale" data-testid="foot-wholesale">Wholesale</Link>
        {tg && <a href={tg} target="_blank" rel="noreferrer" data-testid="foot-tg">Telegram</a>}
      </div>
      <div>{c.footer_tagline || "2HIGH · MEMBERS LOFT · PRIVATE COLLECTION · 21+ · CRAFTED INDOOR"}</div>
      {c.footer_legal && (
        <div style={{ marginTop: 8 }}>{c.footer_legal}</div>
      )}
    </footer>
  );
}

function InquireFabButton() {
  return (
    <button
      className="inq-fab"
      id="inqFab"
      data-testid="inq-fab-btn"
      aria-label="Open inquiry panel"
      onClick={() => window.dispatchEvent(new CustomEvent("2high:inquire", { detail: {} }))}
    >
      <svg viewBox="0 0 24 24"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
      <span>Inquire</span>
    </button>
  );
}
